import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  effect,
  ElementRef,
  EventEmitter,
  inject,
  OnDestroy,
  OnInit,
  Output,
  signal,
  ViewChild,
  WritableSignal,
} from '@angular/core';
import { CommonModule, DOCUMENT } from '@angular/common';
import { SvgIconComponent } from '@ngneat/svg-icon';
import { ButtonComponent } from '../button/button.component';
import { MegamenuFooterComponent } from './megamenu-footer/megamenu-footer.component';
import { MediaMatcher } from '@angular/cdk/layout';
import { filter, fromEvent, take, takeWhile } from 'rxjs';
import { TranslocoModule } from '@jsverse/transloco';
import { MegamenuSectionComponent } from './megamenu-section/megamenu-section.component';
import { MegamenuTriggerComponent } from './megamenu-trigger/megamenu-trigger.component';
import { A11yModule } from '@angular/cdk/a11y';
import { NavigationStart, Router, RouterLink } from '@angular/router';
import { IS_BROWSER } from '../../providers/is-platform.provider';
import { ScrollLockService } from '../../services/scroll-lock.service';
import { AddLocalePipe } from '../../pipes/url-locale.pipe';
import { MenuService } from '../../services/menu.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-megamenu',
  imports: [
    CommonModule,
    SvgIconComponent,
    ButtonComponent,
    MegamenuFooterComponent,
    TranslocoModule,
    MegamenuSectionComponent,
    MegamenuTriggerComponent,
    A11yModule,
    RouterLink,
    AddLocalePipe,
  ],
  templateUrl: './megamenu.component.html',
  styleUrls: ['./megamenu.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MegamenuComponent implements OnInit, OnDestroy {
  private readonly document = inject(DOCUMENT);
  private readonly mediaMatcher = inject(MediaMatcher);
  private readonly mediaQuery = this.mediaMatcher.matchMedia('(min-width: 64em)');
  private readonly scrollService = inject(ScrollLockService);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);

  @ViewChild('nav')
  private nav!: ElementRef<HTMLElement>;

  @ViewChild(MegamenuTriggerComponent)
  private firstTrigger?: MegamenuTriggerComponent;

  @Output()
  isOpened = new EventEmitter<boolean>();

  readonly isBrowser = inject(IS_BROWSER);
  isDesktop = this.mediaQuery.matches;
  categories = inject(MenuService).getHeaderNavigation();

  openedCategoryTrigger: WritableSignal<MegamenuTriggerComponent | null> = signal(null);
  mobileOpened = signal(false);
  submenuOpened = signal(false);
  navOverflow = signal('initial');
  ulHeight = signal('unset');

  constructor() {
    effect(() => {
      const isOpened =
        !!this.openedCategoryTrigger() || this.mobileOpened() || this.submenuOpened();
      this.isOpened.emit(isOpened);
    });
  }

  ngOnInit() {
    if (this.isBrowser) {
      this.mediaQuery.addEventListener('change', this.onMediaQueryChange);
      this.router.events
        .pipe(
          filter((e) => e instanceof NavigationStart),
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe(() => {
          this.forceClose();
        });
    }
  }

  ngOnDestroy() {
    if (this.isBrowser) {
      this.mediaQuery.removeEventListener('change', this.onMediaQueryChange);
      this.scrollService.unlock();
    }
  }

  onKeydown(e: KeyboardEvent): void {
    if (e.key !== 'Escape') {
      return;
    }

    e.preventDefault();
    this.closeSubmenuOrMobileMenu();
  }

  onTriggerClick(trigger: MegamenuTriggerComponent): void {
    this.toggleSubmenu(trigger);
  }

  forceClose(): void {
    this.closeSubmenu();
    this.closeMobile();
  }

  toggleSubmenu(target: MegamenuTriggerComponent): void {
    const isExpanded = this.openedCategoryTrigger() === target;
    isExpanded ? this.closeSubmenu() : this.openSubmenu(target);
  }

  openMobile(): void {
    if (this.isDesktop) {
      return;
    }

    this.mobileOpened.set(true);
    setTimeout(() => {
      // todo - maybe somehow try to call it again once it becomes defined?
      this.firstTrigger?.focus();
      this.scrollService.lock();
    });
  }

  openSubmenu(target: MegamenuTriggerComponent): void {
    this.closeSubmenu();

    this.submenuOpened.set(true);

    this.openedCategoryTrigger.set(target);

    fromEvent(this.document, 'scroll', { passive: true })
      .pipe(
        takeWhile(() => this.openedCategoryTrigger() !== null),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => this.forceClose());

    setTimeout(() => {
      this.openedCategoryTrigger()?.section.focus();
    });

    if (this.isDesktop) {
      return;
    }

    this.navOverflow.set('initial');

    fromEvent(this.nav.nativeElement, 'transitionend', { passive: true, once: true })
      .pipe(take(1))
      .subscribe(() => {
        this.navOverflow.set('hidden scroll');
        this.ulHeight.set(this.openedCategoryTrigger()?.section?.offsetHeight + 'px');
      });
  }

  closeSubmenuOrMobileMenu(): void {
    if (this.openedCategoryTrigger()) {
      this.closeSubmenu();

      return;
    }

    this.closeMobile();
  }

  closeMobile(): void {
    if (!this.isDesktop) {
      this.mobileOpened.set(false);
      this.scrollService.unlock();
    }
  }

  closeSubmenu(): void {
    const trigger = this.openedCategoryTrigger();

    if (!trigger) {
      return;
    }

    this.submenuOpened.set(false);

    if (!this.isDesktop) {
      this.navOverflow.set('initial');
      this.ulHeight.set('unset');
    }

    trigger.focus();
    this.openedCategoryTrigger.set(null);
  }

  private onMediaQueryChange = (e: MediaQueryListEvent): void => {
    this.isDesktop = e.matches;
  };
}
